import classNames from 'classnames'
import React from 'react'

enum TagSize {
  none = '',
  small = 'h-4 min-w-4', // 16px height
  medium = 'h-5 min-w-5', // 20px height
  large = 'min-w-5',
}

type TagSizeType = keyof typeof TagSize

enum TagPadding {
  none = 'p-0',
  small = 'px-[5px] py-0', // 16px height
  medium = 'px-1.5 py-0.5', // 20px height
  large = 'p-3',
}

type TagPaddingType = keyof typeof TagPadding

enum TagFont {
  none = '',
  small = 'text-3xs leading-3', // 10px/12px
  medium = 'text-2xs leading-4', // 12px/16px
  large = 'text-sm',
}

enum TagVariant {
  filled = 'border border-bg-primary bg-gray-50 text-gray-600 font-medium dark:bg-gray-600 dark:text-gray-50',
  bordered = 'border border-primary text-muted',
  disabled = 'border border-gray-400 text-disabled',
  accent = 'bg-accent text-link',
  neutral = 'bg-neutral text-muted font-medium',
  custom = '',
}

type TagVariantType = keyof typeof TagVariant

enum TagRounding {
  none = '',
  small = 'rounded-sm',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  medium = 'rounded-sm',
  large = 'rounded-md',
  full = 'rounded-full',
}

type TagProps = {
  size?: TagSizeType
  rounding?: keyof typeof TagRounding
  padding?: TagPaddingType
  variant?: TagVariantType
  className?: string
  dataTestId?: string
}

export function Tag({
  className,
  size = 'medium',
  rounding,
  padding = size,
  variant = 'filled',
  dataTestId,
  children,
}: React.PropsWithChildren<TagProps>) {
  const tagPadding = TagPadding[padding]
  const tagSize = TagSize[size]
  const tagFont = TagFont[size]
  const tagRounding = TagRounding[rounding ?? size]
  const tagVariant = TagVariant[variant]

  return (
    <span
      className={classNames(
        className,
        'inline-flex items-center justify-center',
        tagPadding,
        tagRounding,
        tagVariant,
        tagSize,
        tagFont
      )}
      data-testid={dataTestId ? `${dataTestId}-tag` : undefined}
    >
      {children}
    </span>
  )
}
